import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const ExportModal = ({ isOpen, onClose, onExport }) => {
  const [format, setFormat] = useState('pdf');
  const [includeCharts, setIncludeCharts] = useState(true);
  const [includeTables, setIncludeTables] = useState(true);
  const [includeMetrics, setIncludeMetrics] = useState(true);

  if (!isOpen) return null;

  const handleExport = () => {
    onExport({
      format,
      includeCharts,
      includeTables,
      includeMetrics
    });
    onClose();
  };

  const formats = [
    { value: 'pdf', label: 'PDF Document', icon: 'FileText', description: 'Best for printing and sharing' },
    { value: 'excel', label: 'Excel Spreadsheet', icon: 'FileSpreadsheet', description: 'Editable data format' },
    { value: 'csv', label: 'CSV File', icon: 'Table', description: 'Raw data export' }
  ];

  return (
    <div className="fixed inset-0 z-300 flex items-center justify-center p-24 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg border border-border shadow-elevation-5 w-full max-w-[500px] max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-24 border-b border-border">
          <div className="flex items-center gap-12">
            <Icon name="Download" size={24} color="var(--color-primary)" strokeWidth={2} />
            <h2 className="font-heading font-semibold text-h4 text-foreground">
              Export Report
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-[36px] h-[36px] flex items-center justify-center rounded-lg hover:bg-muted transition-smooth"
            aria-label="Close export modal"
          >
            <Icon name="X" size={20} color="var(--color-foreground)" strokeWidth={2} />
          </button>
        </div>

        <div className="p-24">
          <div className="mb-24">
            <label className="font-body font-medium text-[14px] text-foreground mb-12 block">
              Export Format
            </label>
            <div className="space-y-8">
              {formats?.map((fmt) => (
                <button
                  key={fmt?.value}
                  onClick={() => setFormat(fmt?.value)}
                  className={`
                    w-full flex items-start gap-12 p-18 rounded-lg border-2 transition-smooth
                    ${format === fmt?.value
                      ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/50'
                    }
                  `}
                >
                  <Icon
                    name={fmt?.icon}
                    size={24}
                    color={format === fmt?.value ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                    strokeWidth={2}
                    className="flex-shrink-0 mt-[2px]"
                  />
                  <div className="flex-1 text-left">
                    <div className="font-body font-semibold text-[14px] text-foreground mb-4">
                      {fmt?.label}
                    </div>
                    <div className="font-caption text-[12px] text-muted-foreground">
                      {fmt?.description}
                    </div>
                  </div>
                  <div className={`
                    w-[20px] h-[20px] rounded-full border-2 flex items-center justify-center flex-shrink-0
                    ${format === fmt?.value ? 'border-primary' : 'border-border'}
                  `}>
                    {format === fmt?.value && (
                      <div className="w-[10px] h-[10px] rounded-full bg-primary" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="mb-24">
            <label className="font-body font-medium text-[14px] text-foreground mb-12 block">
              Include in Export
            </label>
            <div className="space-y-12">
              <Checkbox
                label="Charts and Visualizations"
                description="Include all chart graphics"
                checked={includeCharts}
                onChange={(e) => setIncludeCharts(e?.target?.checked)}
              />
              <Checkbox
                label="Data Tables"
                description="Include detailed data tables"
                checked={includeTables}
                onChange={(e) => setIncludeTables(e?.target?.checked)}
              />
              <Checkbox
                label="Key Metrics"
                description="Include summary metrics"
                checked={includeMetrics}
                onChange={(e) => setIncludeMetrics(e?.target?.checked)}
              />
            </div>
          </div>

          <div className="flex gap-12">
            <Button
              variant="outline"
              onClick={onClose}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleExport}
              iconName="Download"
              iconPosition="left"
              fullWidth
            >
              Export Report
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;